import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Trash2, ChevronDown, ChevronUp, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../context/AuthContext";
import { imgSrc } from "../context/ContentContext";
import { btnGhost, inputClass } from "./fields";

const STATUSES = ["nieuw", "in behandeling", "offerte verstuurd", "afgerond", "gearchiveerd"];
const statusColor = {
  nieuw: "bg-cream text-[#111111]",
  "in behandeling": "bg-[#111111] text-white",
  "offerte verstuurd": "bg-[#3B3B38] text-white",
  afgerond: "bg-[#DDE8D8] text-[#2F4A2A]",
  gearchiveerd: "bg-[#E2E0D9] text-[#555555]",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Row = ({ label, value }) =>
  value ? (
    <div>
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.15em] text-[#8E8D8A]">{label}</p>
      <p className="text-sm text-[#111111]">{value}</p>
    </div>
  ) : null;

const RequestCard = ({ item, onStatus, onDelete }) => {
  const [open, setOpen] = useState(item.status === "nieuw");
  const adres = [item.adres, [item.postcode, item.plaats].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <article data-testid={`request-card-${item.id}`} className="border border-[#DDD9D0] bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-lg font-bold uppercase tracking-wide text-[#111111]">{item.naam}</h3>
            <span data-testid="request-status-badge" className={`px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-[0.15em] ${statusColor[item.status] || statusColor.nieuw}`}>
              {item.status || "nieuw"}
            </span>
          </div>
          <p className="mt-1 text-sm text-[#555555]">
            {item.dienst} · {fmtDate(item.created_at)} {item.fotos?.length ? `· ${item.fotos.length} foto's` : ""}
          </p>
        </div>
        {open ? <ChevronUp className="h-5 w-5 shrink-0 text-[#8E8D8A]" /> : <ChevronDown className="h-5 w-5 shrink-0 text-[#8E8D8A]" />}
      </button>
      {open && (
        <div className="border-t border-[#E2E0D9] px-5 py-5">
          <div className="grid gap-5 lg:grid-cols-3">
            <div className="space-y-3">
              <a href={`tel:${item.telefoon}`} className="flex items-center gap-2 text-sm text-[#111111] hover:underline"><Phone className="h-4 w-4 text-[#8E8D8A]" />{item.telefoon || "geen nummer"}</a>
              <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-sm text-[#111111] hover:underline"><Mail className="h-4 w-4 text-[#8E8D8A]" />{item.email}</a>
              {adres && <p className="flex items-center gap-2 text-sm text-[#111111]"><MapPin className="h-4 w-4 text-[#8E8D8A]" />{adres}</p>}
              <Row label="Contactvoorkeur" value={item.contact_voorkeur} />
              <Row label="Planning" value={item.planning} />
              <Row label="Budget" value={item.budget} />
            </div>
            <div className="lg:col-span-2">
              <p className="font-display text-[10px] font-bold uppercase tracking-[0.15em] text-[#8E8D8A]">Omschrijving</p>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-[#333333]">{item.bericht}</p>
              {item.fotos?.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {item.fotos.map((f) => (
                    <a key={f} href={imgSrc(f)} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden border border-[#D8D6D0] bg-[#F5F4F1]">
                      <img src={imgSrc(f)} alt="Bijlage" className="h-full w-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
              {!item.fotos?.length && <p className="mt-3 flex items-center gap-1.5 text-xs text-[#8E8D8A]"><ImageOff className="h-3.5 w-3.5" /> Geen foto's bijgevoegd</p>}
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#E2E0D9] pt-4">
            <select data-testid="request-status-select" value={item.status || "nieuw"} onChange={(e) => onStatus(item.id, e.target.value)} className={`${inputClass} w-auto`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="button" data-testid="request-delete" onClick={() => onDelete(item.id)} className={`${btnGhost} hover:border-red-600 hover:text-red-600`}>
              <Trash2 className="h-4 w-4" /> Verwijderen
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export default function AdminAanvragen() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("actief");

  const load = () =>
    adminApi.get("/admin/quote-requests").then(({ data }) => setItems(data)).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);

  const onStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/quote-requests/${id}`, { status });
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Deze aanvraag definitief verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/quote-requests/${id}`);
      setItems((list) => list.filter((it) => it.id !== id));
      toast.success("Aanvraag verwijderd");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const visible = (items || []).filter((it) =>
    filter === "alles" ? true : filter === "actief" ? !["afgerond", "gearchiveerd"].includes(it.status) : it.status === filter
  );
  const count = (s) => (items || []).filter((it) => (it.status || "nieuw") === s).length;

  return (
    <div data-testid="admin-aanvragen-page">
      <h1 className="font-display text-3xl font-bold uppercase tracking-wide text-[#111111]">Aanvragen</h1>
      <p className="mt-1 text-sm text-[#555555]">{items ? `${items.length} aanvragen, ${count("nieuw")} nieuw` : "Laden..."}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {["actief", "nieuw", ...STATUSES.filter((s) => s !== "nieuw"), "alles"].map((f) => (
          <button key={f} type="button" data-testid={`requests-filter-${f.replace(/\s+/g, "-")}`} onClick={() => setFilter(f)} className={`rounded-[4px] border px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-wide ${filter === f ? "border-[#111111] bg-[#111111] text-white" : "border-[#D8D6D0] bg-white text-[#555555]"}`}>
            {f}
          </button>
        ))}
      </div>
      <div className="mt-6 space-y-3">
        {visible.map((it) => <RequestCard key={it.id} item={it} onStatus={onStatus} onDelete={onDelete} />)}
        {items && visible.length === 0 && <p data-testid="requests-empty" className="text-sm text-[#8E8D8A]">Geen aanvragen in deze weergave.</p>}
      </div>
    </div>
  );
}
